import type { Subscription } from "../../../features/users/usersTypes";
import { SubscriptionType } from "../../../features/users/usersTypes";

export function getActiveSubscription(
  subscriptions?: Subscription[] | null
): Subscription | null {
  if (!subscriptions || subscriptions.length === 0) return null;

  const active = subscriptions.find(
    (subscription) => subscription.status?.toLowerCase() === "active"
  );
  return active ?? null;
}

export function formatSubscriptionType(
  subscriptionType?: SubscriptionType | null
): string {
  switch (subscriptionType) {
    case SubscriptionType.MONTHLY:
      return "Monthly";
    case SubscriptionType.YEARLY:
      return "Yearly";
    case SubscriptionType.QUARTERLY:
      return "Quarterly";
    case SubscriptionType.HALF_YEARLY:
      return "Half-yearly";
    default:
      return "—";
  }
}

export function formatSubscriptionDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatPlanAmount(amount?: string | null): string {
  if (amount == null || String(amount).trim() === "") return "—";
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return amount;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
}

export function formatSubscriptionStatus(status?: string | null): string {
  const value = status?.trim();
  if (!value) return "—";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function getTrialLabel(subscription?: Subscription | null): string | null {
  const trialDays = subscription?.trial_days;
  if (trialDays == null) return null;
  if (trialDays > 0) return `${trialDays}-day free trial`;
  return null;
}

function addSubscriptionInterval(
  date: Date,
  subscriptionType?: SubscriptionType | null
): Date | null {
  const next = new Date(date);
  switch (subscriptionType) {
    case SubscriptionType.MONTHLY:
      next.setMonth(next.getMonth() + 1);
      return next;
    case SubscriptionType.QUARTERLY:
      next.setMonth(next.getMonth() + 3);
      return next;
    case SubscriptionType.HALF_YEARLY:
      next.setMonth(next.getMonth() + 6);
      return next;
    case SubscriptionType.YEARLY:
      next.setFullYear(next.getFullYear() + 1);
      return next;
    default:
      return null;
  }
}

export function getNextChargeDate(subscription?: Subscription | null): string {
  if (!subscription) return "—";
  const base = subscription.last_charged_on || subscription.started_on;
  if (!base) return "—";
  const baseDate = new Date(base);
  if (Number.isNaN(baseDate.getTime())) return "—";
  const next = addSubscriptionInterval(baseDate, subscription.subscription_type);
  if (!next) return "—";
  return formatSubscriptionDate(next.toISOString());
}
